// BULK Trader Revamp
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import { DateTime } from '@components/misc/dateTime/dateTime';
import type { TranslatedString } from '@interfaces/shared';
import { ExternalLink } from '@ovComponents/1-primative/link/externalLink';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  type AllocationFileRecord,
  type BulkTradeFileRecord,
  getAllocationFileDownloadLabel,
  getBulkTradeFileDownloadLabel,
} from '@ovComponents/4-module/bulkTrader/bulkTradeFileLabels';
import { BULK_TRADER_REVAMP_BASE_PATH } from '@ovComponents/4-module/bulkTrader/paths';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import {
  calculateDurationSeconds,
  summarizeInteractedFields,
} from '@utils/analytics/eventMetrics';
import { usePageState } from '@utils/usePageState';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type BulkTradeRunListRow = {
  id: string;
  createdAt: string;
  buyBulkTradeFileUrl?: string | null;
  sellBulkTradeFileUrl?: string | null;
  mutualFundFileUrl?: string | null;
  state: string;
  bulkPTFFileUrls?: string[] | null;
  allocationFileUrls?: string[] | null;
  ptfAllocationFileUrls?: string[] | null;
  bulkTradeFiles?: BulkTradeFileRecord[] | null;
  allocationFiles?: AllocationFileRecord[] | null;
  organization: {
    translatedName: TranslatedString;
  };
};

const FETCH_BULK_TRADE_RUNS = gql`
  query fetchBulkTradeRuns($input: FetchBulkTradeRunsInput!) {
    fetchBulkTradeRuns(input: $input) {
      bulkTradeRuns {
        id
        createdAt
        buyBulkTradeFileUrl
        sellBulkTradeFileUrl
        mutualFundFileUrl
        allocationFileUrls
        state
        bulkPTFFileUrls
        ptfAllocationFileUrls
        bulkTradeFiles {
          type
          requestType
          url
        }
        allocationFiles {
          requestType
          url
          type
        }
        organization {
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
      }
      totalCount
    }
  }
`;

export const CREATE_BULK_TRADE_RUN = gql`
  mutation generateBulkTrades($input: GenerateBulkTradesInput!) {
    generateBulkTrades(input: $input) {
      bulkTradeRun {
        id
      }
    }
  }
`;

const BulkTradersTable = () => {
  const canWriteBulkTradeRun = usePermission('write:bulk_trade_run');
  const { activeOrganization } = useContext(UserContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['bulkTrader']);
  const navigate = useNavigate();
  const [page, setPage] = usePageState(1, 'page');
  const pageSize = 15;
  const [runStartTime, setRunStartTime] = useState<Date | null>(null);
  const [interactedFields, setInteractedFields] = useState<string[]>([]);
  const { loading, error, data, previousData } = useQuery(
    FETCH_BULK_TRADE_RUNS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
          },
          pagination: {
            sortField: 'createdAt',
            sortDesc: true,
            perPage: pageSize,
            offSet: (page - 1) * pageSize,
          },
        },
      },
    },
  );

  const [newBulkTrader, { data: newData, loading: newLoading }] = useMutation(
    CREATE_BULK_TRADE_RUN,
  );

  useEffect(() => {
    if (newData) {
      const completionTime = new Date();
      const formDuration = calculateDurationSeconds(runStartTime, {
        endTime: completionTime,
        round: true,
      });

      // Track bulk trader run completion
      const { fieldInteractionCount, interactedFields: interactedFieldNames } =
        summarizeInteractedFields(interactedFields);
      sendAnalytic(analyticsEvents.bulkTraderRunComplete, {
        bulkTradeRunId: newData.generateBulkTrades.bulkTradeRun.id,
        organizationId: activeOrganization.id,
        fieldInteractionCount,
        interactedFields: interactedFieldNames,
        formDuration,
        timestamp: completionTime.toISOString(),
      });

      // Reset tracking state
      setRunStartTime(null);
      setInteractedFields([]);

      navigate(
        `${BULK_TRADER_REVAMP_BASE_PATH}/${newData.generateBulkTrades.bulkTradeRun.id}`,
      );
    }
  }, [
    newData,
    navigate,
    runStartTime,
    interactedFields,
    activeOrganization.id,
    sendAnalytic,
  ]);

  if (error) {
    return (
      <Box data-testid='bulk-traders-table-container'>
        <Typography variant='bodySmall' component='p'>
          Error
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid='bulk-traders-table-container'>
      {loading && !previousData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='bulk-traders-loading'
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : (
        <>
          {canWriteBulkTradeRun && (
            <Stack
              data-testid='bulk-traders-actions'
              direction='row'
              sx={{
                justifyContent: 'flex-end',
                p: 2,
              }}
            >
              <Button
                disabled={newLoading}
                variant='contained'
                onClick={() => {
                  const startTime = new Date();
                  setRunStartTime(startTime);
                  setInteractedFields([]);

                  // Track bulk trader page list create
                  sendAnalytic(analyticsEvents.bulkTraderPageListCreate, {
                    organizationId: activeOrganization.id,
                    timestamp: startTime.toISOString(),
                  });
                  // Track bulk trader run initiation
                  sendAnalytic(analyticsEvents.bulkTraderRunInit, {
                    organizationId: activeOrganization.id,
                    timestamp: startTime.toISOString(),
                  });
                  newBulkTrader({
                    variables: {
                      input: {
                        autoFillActualQuantity: false,
                        organizationId: activeOrganization.id,
                      },
                    },
                  });
                }}
                data-testid='bulk-traders-start-new-button'
              >
                {t('startNew')}
              </Button>
            </Stack>
          )}
          <Table
            sx={{ minWidth: 650 }}
            aria-label='table'
            data-testid='bulk-traders-table'
          >
            <TableHead>
              <TableRow>
                <TableHeadCell data-testid='bulk-traders-header-created-at'>
                  {t('table.createdAt')}
                </TableHeadCell>
                <TableHeadCell data-testid='bulk-traders-header-files'>
                  {t('table.files')}
                </TableHeadCell>
                <TableHeadCell data-testid='bulk-traders-header-organization'>
                  {t('table.organization')}
                </TableHeadCell>
                <TableHeadCell right data-testid='bulk-traders-header-state'>
                  {t('table.state')}
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || previousData)?.fetchBulkTradeRuns?.bulkTradeRuns?.map(
                (bulkTradeRun: BulkTradeRunListRow, index: number) => (
                  <TableRow
                    hover
                    pointer
                    key={bulkTradeRun.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      textDecoration: 'none',
                    }}
                    onClick={() => {
                      // Track bulk trader row selection
                      sendAnalytic(
                        analyticsEvents.bulkTraderPageListRowSelect,
                        {
                          bulkTradeRunId: bulkTradeRun.id,
                          state: bulkTradeRun.state,
                          organizationName: translateBackend(
                            bulkTradeRun.organization.translatedName,
                          ),
                          timestamp: new Date().toISOString(),
                        },
                      );
                      navigate(
                        `${BULK_TRADER_REVAMP_BASE_PATH}/${bulkTradeRun.id}`,
                      );
                    }}
                    data-testid={`bulk-traders-row-${index}`}
                  >
                    <TableCell data-testid={`bulk-traders-created-at-${index}`}>
                      <DateTime
                        variant='titleMedium'
                        date={bulkTradeRun.createdAt}
                      />
                    </TableCell>
                    <TableCell data-testid={`bulk-traders-files-${index}`}>
                      <Stack
                        direction='row'
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap' }}
                      >
                        {bulkTradeRun.buyBulkTradeFileUrl && (
                          <ExternalLink
                            onClick={(e) => {
                              e.stopPropagation();
                              sendAnalytic(
                                analyticsEvents.bulkTraderPageListDownloadBuyFile,
                                {
                                  bulkTradeRunId: bulkTradeRun.id,
                                  fileType: 'buy',
                                  timestamp: new Date().toISOString(),
                                },
                              );
                            }}
                            href={bulkTradeRun.buyBulkTradeFileUrl}
                            data-testid={`bulk-traders-download-buy-file-${index}`}
                          >
                            {t('buyTradeFile')}
                          </ExternalLink>
                        )}
                        {bulkTradeRun.sellBulkTradeFileUrl && (
                          <ExternalLink
                            onClick={(e) => {
                              e.stopPropagation();
                              sendAnalytic(
                                analyticsEvents.bulkTraderPageListDownloadSellFile,
                                {
                                  bulkTradeRunId: bulkTradeRun.id,
                                  fileType: 'sell',
                                  timestamp: new Date().toISOString(),
                                },
                              );
                            }}
                            href={bulkTradeRun.sellBulkTradeFileUrl}
                            data-testid={`bulk-traders-download-sell-file-${index}`}
                          >
                            {t('sellTradeFile')}
                          </ExternalLink>
                        )}
                        {bulkTradeRun.mutualFundFileUrl && (
                          <ExternalLink
                            onClick={(e) => {
                              e.stopPropagation();
                              sendAnalytic(
                                analyticsEvents.bulkTraderPageListDownloadMutualFundFile,
                                {
                                  bulkTradeRunId: bulkTradeRun.id,
                                  fileType: 'mutualFund',
                                  timestamp: new Date().toISOString(),
                                },
                              );
                            }}
                            href={bulkTradeRun.mutualFundFileUrl}
                            data-testid={`bulk-traders-download-mutual-fund-file-${index}`}
                          >
                            {t('mutualFundTradeFile')}
                          </ExternalLink>
                        )}
                        {(bulkTradeRun.bulkPTFFileUrls ?? []).map(
                          (fileUrl: string, fileIndex: number) => (
                            <ExternalLink
                              key={fileUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                sendAnalytic(
                                  analyticsEvents.bulkTraderPageListDownloadPtfFile,
                                  {
                                    bulkTradeRunId: bulkTradeRun.id,
                                    fileType: 'ptf',
                                    fileIndex: fileIndex + 1,
                                    timestamp: new Date().toISOString(),
                                  },
                                );
                              }}
                              href={fileUrl}
                              data-testid={`bulk-traders-download-ptf-file-${index}-${fileIndex}`}
                            >{`${t('ptfBulkTradeFile')}: ${fileIndex + 1}`}</ExternalLink>
                          ),
                        )}
                        {(bulkTradeRun.allocationFileUrls ?? []).map(
                          (fileUrl: string, fileIndex: number) => (
                            <ExternalLink
                              key={fileUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                sendAnalytic(
                                  analyticsEvents.bulkTraderPageListDownloadAllocationFile,
                                  {
                                    bulkTradeRunId: bulkTradeRun.id,
                                    fileType: 'allocation',
                                    fileIndex: fileIndex + 1,
                                    timestamp: new Date().toISOString(),
                                  },
                                );
                              }}
                              href={fileUrl}
                              data-testid={`bulk-traders-download-allocation-file-${index}-${fileIndex}`}
                            >{`${t('allocationFile')}: ${fileIndex + 1}`}</ExternalLink>
                          ),
                        )}
                        {(bulkTradeRun.ptfAllocationFileUrls ?? []).map(
                          (fileUrl: string, fileIndex: number) => (
                            <ExternalLink
                              key={fileUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                sendAnalytic(
                                  analyticsEvents.bulkTraderPageListDownloadPtfAllocationFile,
                                  {
                                    bulkTradeRunId: bulkTradeRun.id,
                                    fileType: 'ptfAllocation',
                                    fileIndex: fileIndex + 1,
                                    timestamp: new Date().toISOString(),
                                  },
                                );
                              }}
                              href={fileUrl}
                              data-testid={`bulk-traders-download-ptf-allocation-file-${index}-${fileIndex}`}
                            >{`${t('ptfAllocationFile')}: ${fileIndex + 1}`}</ExternalLink>
                          ),
                        )}
                        {bulkTradeRun.bulkTradeFiles?.map(
                          (file: BulkTradeFileRecord, fileIndex: number) => {
                            const files = bulkTradeRun.bulkTradeFiles ?? [];
                            const sameTypeCount = files
                              .slice(0, fileIndex + 1)
                              .filter(
                                (f: BulkTradeFileRecord) =>
                                  f.type === file.type,
                              ).length;

                            const totalSameType = files.filter(
                              (f: BulkTradeFileRecord) => f.type === file.type,
                            ).length;

                            const fileLabel = getBulkTradeFileDownloadLabel(
                              file,
                              t,
                              sameTypeCount,
                              totalSameType,
                            );

                            return (
                              <ExternalLink
                                key={file.url}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderPageListDownloadBulkTradeFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: file.type,
                                      fileIndex: fileIndex + 1,
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={file.url}
                                data-testid={`bulk-traders-download-bulk-trade-file-${index}-${fileIndex}`}
                              >
                                {fileLabel}
                              </ExternalLink>
                            );
                          },
                        )}
                        {bulkTradeRun.allocationFiles?.map(
                          (file: AllocationFileRecord, fileIndex: number) => {
                            const files = bulkTradeRun.allocationFiles ?? [];
                            const sameTypeCount = files
                              .slice(0, fileIndex + 1)
                              .filter(
                                (f: AllocationFileRecord) =>
                                  f.type === file.type,
                              ).length;

                            const totalSameType = files.filter(
                              (f: AllocationFileRecord) => f.type === file.type,
                            ).length;

                            const fileLabel = getAllocationFileDownloadLabel(
                              file,
                              t,
                              sameTypeCount,
                              totalSameType,
                            );

                            return (
                              <ExternalLink
                                key={file.url}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderPageListDownloadAllocationFileNew,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: file.type,
                                      fileIndex: fileIndex + 1,
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={file.url}
                                data-testid={`bulk-traders-download-allocation-file-new-${index}-${fileIndex}`}
                              >
                                {fileLabel}
                              </ExternalLink>
                            );
                          },
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      data-testid={`bulk-traders-organization-${index}`}
                    >
                      {translateBackend(
                        bulkTradeRun.organization.translatedName,
                      )}
                    </TableCell>
                    <TableCell
                      right
                      data-testid={`bulk-traders-state-${index}`}
                    >
                      <Badge
                        label={bulkTradeRun.state}
                        color={
                          bulkTradeRun.state === 'COMPLETED'
                            ? 'positive'
                            : 'warning'
                        }
                        testId={`bulk-traders-state-chip-${index}`}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
          <Box
            data-testid='bulk-traders-pagination'
            sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
          >
            <Pagination
              showPerPage={false}
              count={Math.ceil(
                ((data || previousData)?.fetchBulkTradeRuns?.totalCount ?? 0) /
                  pageSize,
              )}
              page={page}
              onChange={(_e, newPage) => {
                // Track pagination analytics
                let eventName: string =
                  analyticsEvents.bulkTraderPageListPageNumber;
                if (newPage === page + 1) {
                  eventName = analyticsEvents.bulkTraderPageListPageNext;
                } else if (newPage === page - 1) {
                  eventName = analyticsEvents.bulkTraderPageListPagePrevious;
                }

                sendAnalytic(eventName, {
                  currentPage: page,
                  newPage,
                  totalPages: Math.ceil(
                    ((data || previousData)?.fetchBulkTradeRuns?.totalCount ??
                      0) / pageSize,
                  ),
                  timestamp: new Date().toISOString(),
                });

                setPage(newPage);
              }}
              sx={{ p: 1 }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default BulkTradersTable;
